// CSS import request-range scanners for passthrough import rewriting.
// Example: `@import url("./app.css") screen;` yields the request token boundaries.

// Resolve request token range in one `@import` declaration.
export function locateImportRequestRange(statement: string): { start: number; end: number } | null {
  const lower = statement.toLowerCase()
  const importIndex = lower.indexOf('@import')
  // Check whether the statement contains an `@import` token.
  if (importIndex === -1) {
    // Return null when statement has no `@import` token.
    return null
  } else {
    // Continue import request range scanning.
  }

  const index = skipWhitespace(statement, importIndex + '@import'.length)
  // Check whether the declaration still has a request token.
  if (index >= statement.length) {
    // Return null when import declaration has no request token.
    return null
  } else if (statement[index] === '"' || statement[index] === "'") {
    // Continue quoted request token parsing.
    // Return quoted request token boundaries.
    return locateQuotedRange(statement, index)
  } else if (startsWithImportUrlCall(statement, index)) {
    // Continue `url(...)` token parsing.
    // Return `url(...)` request token boundaries.
    return locateUrlRange(statement, index)
  } else {
    // Continue bare token parsing.
    // Return bare request token boundaries.
    return locateBareRange(statement, index)
  }
}

// Resolve quoted request token boundaries.
function locateQuotedRange(statement: string, start: number): { start: number; end: number } | null {
  const quote = statement[start]
  let cursor = start + 1
  // Scan until the matching quote appears or the statement ends.
  while (cursor < statement.length) {
    if (statement[cursor] === quote && statement[cursor - 1] !== '\\') {
      // Return quoted request token boundaries.
      return { start, end: cursor + 1 }
    } else {
      // Continue quoted request scan.
    }
    cursor += 1
  }
  // Return null for unclosed quoted import requests.
  return null
}

// Resolve `url(...)` request token boundaries.
function locateUrlRange(statement: string, start: number): { start: number; end: number } | null {
  const closeIndex = statement.indexOf(')', start + 4)
  // Check whether the `url(...)` token is closed.
  if (closeIndex === -1) {
    // Return null when `url(...)` is not closed.
    return null
  } else {
    // Return `url(...)` token boundaries.
    return { start, end: closeIndex + 1 }
  }
}

// Resolve bare request token boundaries.
function locateBareRange(statement: string, start: number): { start: number; end: number } | null {
  let cursor = start
  // Consume the token until whitespace or statement terminator appears.
  while (cursor < statement.length && !isImportWhitespace(statement[cursor]) && statement[cursor] !== ';') {
    cursor += 1
  }
  // Check whether the bare token is empty.
  if (cursor === start) {
    // Return null when bare token is empty.
    return null
  } else {
    // Return bare-token boundaries.
    return { start, end: cursor }
  }
}

// Resolve the next non-whitespace index in an import statement.
function skipWhitespace(source: string, start: number): number {
  let cursor = start
  // Advance until the next non-whitespace character appears.
  while (cursor < source.length && isImportWhitespace(source[cursor])) {
    cursor += 1
  }
  // Return the first non-whitespace index.
  return cursor
}

// Check CSS import whitespace characters while scanning request tokens.
function isImportWhitespace(char: string): boolean {
  // Return true for CSS whitespace accepted around import requests.
  return char === ' ' || char === '\t' || char === '\n' || char === '\r' || char === '\f'
}

// Check whether the current token starts with `url(`.
function startsWithImportUrlCall(statement: string, index: number): boolean {
  // Return true when the current slice starts with a case-insensitive `url(` token.
  return statement.slice(index, index + 4).toLowerCase() === 'url('
}
